import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useApp } from "@/state/AppContext";

const EMPTY_IDS = { business: [], worker: [], landmark: [], offer: [], event: [], job: [] };

export function useFavoriteIds() {
  const { tenant, auth } = useApp();
  return useQuery({
    queryKey: ["favoriteIds", tenant?.id],
    queryFn: async () => {
      const { data } = await api.get("/favorites/ids", { params: { tenant_id: tenant.id } });
      return data;
    },
    enabled: !!tenant?.id && !!auth,
    initialData: EMPTY_IDS,
  });
}

export function useFavorites() {
  const { tenant, auth } = useApp();
  return useQuery({
    queryKey: ["favorites", tenant?.id],
    queryFn: async () => {
      const { data } = await api.get("/favorites", { params: { tenant_id: tenant.id } });
      return data;
    },
    enabled: !!tenant?.id && !!auth,
  });
}

export function useToggleFavorite() {
  const { tenant } = useApp();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ targetType, targetId }) => {
      const { data } = await api.post("/favorites/toggle", { target_type: targetType, target_id: targetId });
      return data;
    },
    onMutate: async ({ targetType, targetId }) => {
      const key = ["favoriteIds", tenant?.id];
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData(key) ?? EMPTY_IDS;
      const list = previous[targetType] ?? [];
      const next = list.includes(targetId) ? list.filter((id) => id !== targetId) : [...list, targetId];
      queryClient.setQueryData(key, { ...previous, [targetType]: next });
      return { previous };
    },
    onError: (_error, _payload, context) => {
      if (context?.previous) queryClient.setQueryData(["favoriteIds", tenant?.id], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["favoriteIds", tenant?.id] });
      queryClient.invalidateQueries({ queryKey: ["favorites", tenant?.id] });
    },
  });
}
